import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { TuiButton, TuiNotification } from '@taiga-ui/core';
import { TuiButtonLoading } from '@taiga-ui/kit';
import { WorkflowService } from '@core/services/workflow';
import {
  RequestWorkflowsData,
  WorkflowFormValidationError,
} from '@workflows/types';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { WorkflowStepperComponent } from '../workflow-stepper';
import { asyncScheduler } from 'rxjs';
import { AsyncPipe, NgClass } from '@angular/common';
import { DeviceDetectorService } from '@core/services';

export interface FinalPageValidationError {
  workflowTitle: string;
  nodeTitle: string;
  formTitle: string;
  fieldTitle: string;
  fieldKey: string;
  errorMessage: string;
  workflowId: string;
  nodeId: string;
  formId: string;
  formNumericId: number; // Реальный ID формы для навигации
  nodeNumericId: number; // Реальный ID ноды для навигации
  workflowUUID: string; // UUID workflow для навигации
}

@Component({
  selector: 'x-workflow-final-page',
  templateUrl: './workflow-final-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslatePipe,
    TuiButton,
    TuiNotification,
    TuiButtonLoading,
    RouterLink,
    WorkflowStepperComponent,
    AsyncPipe,
    NgClass,
  ],
  host: {
    class: 'workflow-final-page',
  },
})
export class WorkflowFinalPageComponent implements OnInit {
  private readonly workflowService = inject(WorkflowService);
  readonly deviceService = inject(DeviceDetectorService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  public syncData = output<void>();
  public $validationErrors = signal<FinalPageValidationError[]>([]);
  public $baseRoute = input<string>('', { alias: 'baseRoute' });
  public $isSubmitting = signal<boolean>(false);
  /** Реактивно: меняется при обновлении данных регламентов без перехода по страницам */
  public readonly $areWorkflowsCompleted = computed(() =>
    this.workflowService.areWorkflowsCompleted(),
  );
  public readonly $canComplete = computed(() =>
    this.workflowService.canCompleteRequestFromWorkflows(),
  );
  public readonly $pendingWork = this.workflowService.$pendingWork;

  /** Неотправленные формы и фото заявки, которые блокируют завершение */
  public readonly $blockingItems = computed(() => {
    const forms =
      this.workflowService.$requestsWorkflowData()?.workflowNodesForms ?? [];

    return this.$pendingWork()
      .items.filter(item => item.kind !== 'completion')
      .map(item => ({
        ...item,
        formTitle: forms.find(form => form.id === item.formId)?.title ?? '',
      }));
  });

  public readonly $canSubmit = computed(
    () =>
      !this.$areWorkflowsCompleted() &&
      this.$canComplete() &&
      !this.$pendingWork().unknown &&
      this.$blockingItems().length === 0,
  );

  ngOnInit(): void {
    const canComplete = this.workflowService.canCompleteRequestFromWorkflows();

    if (!canComplete) {
      // Проверка права на завершение заявки невозможна в гварде у роута
      // (так как нет еще самой заявки). Поэтому переход на родительский роут выполняем здесь
      void this.router.navigate(['..'], {
        relativeTo: this.activatedRoute,
        replaceUrl: true,
      });

      return;
    }

    // Проверяем, был ли передан autoSubmit через navigation state
    const navigation = this.router.currentNavigation();
    const state = navigation?.extras?.state || (history.state as any);

    if (state?.['autoSubmit'] === true) {
      // Автоматически вызываем submit через небольшую задержку
      // чтобы компонент успел инициализироваться
      asyncScheduler.schedule(() => this.sync(), 100);
    }
  }

  sync(): void {
    // Очищаем ошибки перед отправкой
    this.$validationErrors.set([]);

    // Устанавливаем состояние загрузки
    this.$isSubmitting.set(true);

    this.syncData.emit();
  }

  /**
   * Обработка ошибок валидации от сервера
   */
  handleValidationErrors(
    validationErrors: WorkflowFormValidationError[],
  ): void {
    const wfData = this.workflowService.$requestsWorkflowData();

    if (!wfData) return;

    const errors: FinalPageValidationError[] = [];

    validationErrors.forEach(validationError => {
      const parsed = this.workflowService.parseFieldUrl(
        validationError.fieldUrl,
      );

      if (!parsed) return;

      // fieldUrl содержит ключи узла и формы без префиксов. Ищем по иерархии
      // регламент → узел (по key) → форма этого узла (по key), чтобы не взять
      // одноимённую форму другого узла/регламента
      let requestWorkflow:
        (typeof wfData.requestsWorkflows)[number] | undefined;
      let node:
        (typeof wfData.requestsWorkflows)[number]['nodes'][number] | undefined;
      let formWithPosition:
        (typeof wfData.workflowNodesForms)[number] | undefined;

      // Сервер указывает экземпляр регламента; без него — сначала экземпляр, где форма невалидна
      const candidateWorkflows = validationError.requestWorkflowId
        ? wfData.requestsWorkflows.filter(
            rw => rw.id === validationError.requestWorkflowId,
          )
        : [...wfData.requestsWorkflows].sort(
            (a, b) =>
              Number(this.isFormValidInInstance(wfData, a.id, parsed.formKey)) -
              Number(this.isFormValidInInstance(wfData, b.id, parsed.formKey)),
          );

      for (const rw of candidateWorkflows) {
        if (rw.workflowVersion.workflow.id !== parsed.workflowId) continue;

        const rwNode = rw.nodes.find(n => n.key === parsed.nodeKey);
        const rwForm = rwNode
          ? wfData.workflowNodesForms.find(
              f => f.key === parsed.formKey && f.workflowNodeId === rwNode.id,
            )
          : undefined;

        if (rwNode && rwForm) {
          requestWorkflow = rw;
          node = rwNode;
          formWithPosition = rwForm;
          break;
        }
      }

      if (!requestWorkflow || !node || !formWithPosition) return;

      // Находим поле в схеме формы
      let fieldTitle = parsed.fieldKey;

      for (const group of formWithPosition.schema.groups) {
        const field = group.fields.find((f: any) => f.key === parsed.fieldKey);

        if (field) {
          fieldTitle = field.title || field.key;
          break;
        }
      }

      // Маппим каждую ошибку поля
      validationError.errors.forEach(err => {
        errors.push({
          workflowTitle: requestWorkflow.workflowVersion.workflow.name,
          nodeTitle: node.title,
          formTitle: formWithPosition.title,
          fieldTitle,
          fieldKey: parsed.fieldKey,
          errorMessage: this.workflowService.getValidationErrorMessage(
            err.validationRule,
            'ruleValue' in err ? err.ruleValue : undefined,
          ),
          workflowId: parsed.workflowId,
          nodeId: parsed.nodeKey,
          formId: parsed.formKey,
          formNumericId: formWithPosition.id, // Числовой ID для роутинга
          nodeNumericId: node.id, // Числовой ID для роутинга
          workflowUUID: requestWorkflow.id, // UUID для роутинга
        });
      });
    });

    this.$validationErrors.set(errors);
  }

  private isFormValidInInstance(
    wfData: RequestWorkflowsData,
    requestWorkflowId: string,
    formKey: string,
  ): boolean {
    const formIds = new Set(
      wfData.workflowNodesForms
        .filter(form => form.key === formKey)
        .map(form => form.id),
    );

    return wfData.submissions.some(
      sb =>
        sb.requestWorkflowId === requestWorkflowId &&
        formIds.has(sb.workflowNodeFormId) &&
        sb.isValid,
    );
  }

  /**
   * Сброс состояния загрузки (вызывается из родительского компонента)
   */
  resetSubmitting(): void {
    this.$isSubmitting.set(false);
  }
}
